/**
 * 篮球的规则事件 → 看得见的飘字、读屏播报。纯函数：给事件和事件发生后的状态，交回要画的字与要念的话。
 * 得分、不中的字飘在筐上（球在那儿判的）；擦框、压哨是紧张的表现，不换动作段（F0 修订 B3、B6），只叠一行字。
 * 用词只说游戏里发生了什么，不出现内部代码。
 */
import type { BasketEventBody, BasketMissReason, BasketState } from "../core";
import type { FloatText, FloatTone } from "./feedback";

const MISS_TEXT: Record<BasketMissReason, string> = { short: "偏近", long: "偏远", rim_out: "弹出来了" };
const MISS_SAY: Record<BasketMissReason, string> = { short: "差一点，偏近了。", long: "用力过头，偏远了。", rim_out: "砸到筐沿，弹出来了。" };

/** 一条事件要飘的字；没有就是空数组。 */
export function basketFloatsFor(event: BasketEventBody, state: BasketState, nowMs: number, nextId: () => number): FloatText[] {
  const overHoop = (text: string, tone: FloatTone, lift = 0): FloatText => ({ id: nextId(), text, tone, x: state.hoop.x - 6, y: state.hoop.rimY - 28 - lift, bornMs: nowMs });
  switch (event.type) {
    case "scored": {
      const out = [overHoop(`+${event.points}`, "good")];
      const label = event.swish ? "空心" : event.rimIn ? "滚进去了" : null;
      if (label) out.push(overHoop(label, "info", 18));
      if (event.streak >= 3) out.push(overHoop(`连进 ${event.streak} 个`, "good", label ? 36 : 18));
      return out;
    }
    case "missed":
      return [overHoop(MISS_TEXT[event.reason], "info")];
    case "rim":
      return [overHoop("擦框…", "info", 18)];
    case "buzzer":
      return [overHoop("压哨！", "info", 36)];
    default:
      return [];
  }
}

/** 筐沿紧不紧：擦框、压哨之后紧张，直到这一球判完（进或不中）或者新开一局；别的事件不改 */
export function tenseAfter(event: BasketEventBody, tense: boolean): boolean {
  if (event.type === "rim" || event.type === "buzzer") return true;
  if (event.type === "scored" || event.type === "missed" || event.type === "started") return false;
  return tense;
}

/** 读屏播报：只念改变局面的事；蓄力、出手、落地、接球靠画面与按钮状态，不另念。 */
export function basketAnnouncementFor(event: BasketEventBody, petName: string): string | null {
  switch (event.type) {
    case "started":
      return `开始了。带${petName}投篮：左右走，按住蓄力，松手投出去，还有 ${Math.round(event.durationMs / 1000)} 秒。`;
    case "scored": {
      const parts = [`进了！${event.value === 3 ? "三分球" : "两分球"}，得 ${event.points} 分。`];
      if (event.swish) parts.push("空心。");
      if (event.streak >= 3) parts.push(`连进 ${event.streak} 个。`);
      return parts.join("");
    }
    case "missed":
      return MISS_SAY[event.reason];
    case "buzzer":
      return "时间到了，这一球还在飞。";
    case "ended":
      return event.reason === "time_up" ? `时间到，一共 ${event.result.score} 分。` : null;
    default:
      return null;
  }
}
